import { useState } from 'react';
import { Link, useParams } from 'react-router';
import { Check, Copy, Info } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Tooltip, TooltipTrigger, TooltipContent } from '@/components/ui/tooltip';
import type { InstagramChannel } from '@/types/channel';
import { useWorkspace } from '@/contexts/workspace-context';
import { formatInstagramHandle } from '@/lib/channel-display';
import { workspaceUrl } from '@/lib/workspace-url';
import { ChannelCommonRows } from './channel-common-rows';

export interface InstagramChannelBodyProps {
  // Narrowed to InstagramChannel — this component is IG-only; callers
  // must guard with channel.type === 'instagram' before passing.
  channel: InstagramChannel;
  canEdit: boolean;
  currentWebhookUrl: string | null;
  currentVerifyToken: string | null;
  setShowUrlDialog: (open: boolean) => void;
  setUrlDialogMode: (mode: 'configure' | 'edit') => void;
  setSwitchToCliDialogOpen: (open: boolean) => void;
  // HMAC signing secret — SEPARATE from the verify token. Shown + rotatable
  // only when a webhook destination is configured.
  currentHmacSecret: string | null;
  onRotateHmac: () => void;
  rotatingHmac: boolean;
}

export function InstagramChannelBody({
  channel,
  canEdit,
  currentWebhookUrl,
  currentVerifyToken,
  setShowUrlDialog,
  setUrlDialogMode,
  setSwitchToCliDialogOpen,
  currentHmacSecret,
  onRotateHmac,
  rotatingHmac,
}: InstagramChannelBodyProps) {
  const { channels } = useWorkspace();
  const { ws } = useParams();
  // Mirror of the Page side: the Page this account was connected through.
  const linkedPage = channels.find(
    (c) => c.type === 'facebook' && c.metaResourceId === channel.metaPageId && c.channelStatus === 'connected',
  );
  const [copiedIgId, setCopiedIgId] = useState(false);
  const [copiedUsername, setCopiedUsername] = useState(false);

  async function copyValue(value: string, setCopied: (v: boolean) => void) {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      toast.success('Copied to clipboard');
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error('Failed to copy. Please copy manually.');
    }
  }

  return (
    <div className="mt-2 grid grid-cols-1 md:grid-cols-[200px_1fr] gap-x-10 gap-y-4 text-sm">
      <ChannelCommonRows
        channelId={channel.id}
        canEdit={canEdit}
        currentWebhookUrl={currentWebhookUrl}
        currentVerifyToken={currentVerifyToken}
        setShowUrlDialog={setShowUrlDialog}
        setUrlDialogMode={setUrlDialogMode}
        setSwitchToCliDialogOpen={setSwitchToCliDialogOpen}
        currentHmacSecret={currentHmacSecret}
        onRotateHmac={onRotateHmac}
        rotatingHmac={rotatingHmac}
      />

      {/* Instagram Account ID */}
      <span className="flex items-center gap-1 text-muted-foreground">
        <span>Instagram Account ID</span>
        <Tooltip>
          <TooltipTrigger
            render={
              <button
                type="button"
                aria-label="What is an Instagram Account ID?"
                className="inline-flex items-center justify-center leading-none p-0 text-muted-foreground/60 hover:text-muted-foreground"
              />
            }
          >
            <Info className="size-3.5" />
          </TooltipTrigger>
          <TooltipContent className="max-w-xs">
            Your Instagram Business Account ID at Meta. Use it to find
            this account in Meta Business Manager.
          </TooltipContent>
        </Tooltip>
      </span>
      <span className="flex items-center gap-1">
        <span>{channel.metaResourceId}</span>
        <Button
          variant="ghost"
          size="icon-xs"
          onClick={() => copyValue(channel.metaResourceId, setCopiedIgId)}
          aria-label="Copy Instagram Account ID"
          title="Copy Instagram Account ID"
        >
          {copiedIgId ? <Check className="size-3.5" /> : <Copy className="size-3.5" />}
        </Button>
      </span>

      {/* Instagram Username */}
      <span className="text-muted-foreground">Instagram Username</span>
      <span className="flex items-center gap-1">
        <span>{channel.instagramUsername ? formatInstagramHandle(channel.instagramUsername) : 'N/A'}</span>
        {channel.instagramUsername && (
          <Button
            variant="ghost"
            size="icon-xs"
            onClick={() => copyValue(channel.instagramUsername!, setCopiedUsername)}
            aria-label="Copy Instagram username"
            title="Copy Instagram username"
          >
            {copiedUsername ? <Check className="size-3.5" /> : <Copy className="size-3.5" />}
          </Button>
        )}
      </span>

      {/* Display Name — hide when Meta returns name === username (no real display
          name set on the IG profile) so the UI doesn't render a duplicate row. */}
      {channel.instagramProfileName && channel.instagramProfileName !== channel.instagramUsername && (
        <>
          <span className="text-muted-foreground">Display Name</span>
          <span>{channel.instagramProfileName}</span>
        </>
      )}

      {/* Linked Facebook Page (only when connection_type === 'facebook_login') */}
      {channel.connectionType === 'facebook_login' && (
        <>
          <span className="text-muted-foreground">Linked Facebook Page</span>
          <span>
            {linkedPage && linkedPage.type === 'facebook' && ws ? (
              <Link to={workspaceUrl(ws, `channels/${linkedPage.id}`)} className="underline-offset-4 hover:underline">
                {linkedPage.facebookPageName ?? 'Facebook Page'}
              </Link>
            ) : (
              'Connected via Facebook Login'
            )}
          </span>
        </>
      )}
    </div>
  );
}
